import {
    ReactSocket,
    ReactSocketEvents,
    SharedWorkerEvent,
    SocketError,
    ReactSocketEventHandler,
    ReactSocketNamespaceEvents,
} from './types.socket';
import SocketSharedWorker from './socket.sharedworker';
import io, { ManagerOptions, SocketOptions } from 'socket.io-client';
import { AnyRTMessage, Namespaces } from 'jamespot-user-api';

class Socket implements ReactSocket {
    io: any = null;
    worker: any = undefined;
    supportSharedWorker?: Boolean;
    events: ReactSocketEvents = {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    constructor() {}
    init = (idUser: number) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const self = this;
            this.supportSharedWorker = typeof SharedWorker !== 'undefined';
            if (!this.supportSharedWorker || J.socketLongPolling) {
                const socketOptions: Partial<ManagerOptions & SocketOptions> = {
                    path: '/socket.io4',
                    transports: ['websocket'],
                    upgrade: false,
                };
                if (J.socketLongPolling) {
                    socketOptions.transports = ['polling'];
                    socketOptions.forceBase64 = true;
                }

                this.io = io('//' + window.location.host, socketOptions);

                this.io.io.on('error', (error: SocketError) => {
                    self.callEvent('error', error.message);
                });
                this.io.io.on('reconnect_error', (error: SocketError) => {
                    self.callEvent('reconnect_error', error.message);
                });
                this.io.io.on('reconnect_attempt', (attempt: number) => {
                    self.callEvent('reconnect_attempt', attempt);
                });

                this.io.on('connect', () => {
                    self.callEvent('connect', null);
                });
                this.io.on('disconnect', (reason: string) => {
                    self.callEvent('disconnect', reason);
                });
                this.io.on('message', (m: AnyRTMessage) => {
                    self.callEvent('message', m);
                });
            } else {
                this.worker = new SocketSharedWorker();
                this.worker.port.addEventListener(
                    'message',
                    function (event: SharedWorkerEvent) {
                        const data = event.data;
                        switch (data.type) {
                            case 'socket':
                                self.callEvent(data.event || '', data.data);
                                break;
                        }
                    },
                    false,
                );
                this.worker.port.start();
                this.worker.port.postMessage({
                    type: 'connection',
                    data: { idUser: idUser },
                });
            }
        } catch (ex) {
            console.error('Socket.ts error : ', ex);
        }
    };
    postMessage = (type: string, data: any) => {
        if (this.worker) {
            this.worker.port.postMessage({
                type: type,
                data: data,
            });
        }
    };
    on = (type: string, callback: ReactSocketEventHandler) => {
        if (!this.events[type]) {
            this.events[type] = { all: [] };
        }
        const events = this.events[type];
        if (events) {
            events.all.push(callback);
        }
    };
    onMessage = <T extends keyof Namespaces>(
        namespace: T,
        functionName: Namespaces[T],
        callback: ReactSocketEventHandler,
    ) => {
        this.onMessageInternal(namespace, functionName, callback);
    };
    onMessageInternal = (namespace: string, functionName: string, callback: ReactSocketEventHandler) => {
        if (!this.events['message']) {
            this.events['message'] = { all: [] };
        }
        if (!this.events['message'][namespace]) {
            this.events['message'][namespace] = {};
        }
        const namespaceHandlers = this.events['message'][namespace] as ReactSocketNamespaceEvents;
        if (!namespaceHandlers[functionName]) {
            namespaceHandlers[functionName] = [];
        }
        const namespaceHandlersFunctions = namespaceHandlers[functionName];
        if (namespaceHandlersFunctions) {
            namespaceHandlersFunctions.push(callback);
        }
    };
    offMessage = <T extends keyof Namespaces>(
        namespace: T,
        functionName: Namespaces[T],
        callback: ReactSocketEventHandler,
    ) => {
        this.offMessageInternal(namespace, functionName, callback);
    };
    offMessageInternal = (namespace: string, functionName: string, callback: ReactSocketEventHandler) => {
        if (!this.events['message'] || !this.events['message'][namespace]) {
            return;
        }
        const namespaceHandlers = this.events['message'][namespace] as ReactSocketNamespaceEvents;
        if (namespaceHandlers[functionName]) {
            const namespaceHandlersFunctions = namespaceHandlers[functionName];
            if (namespaceHandlersFunctions) {
                for (let i = namespaceHandlersFunctions.length - 1; i >= 0; i--) {
                    if (namespaceHandlersFunctions[i] === callback) {
                        namespaceHandlersFunctions.splice(i, 1);
                    }
                }
            }
        }
    };
    off = (type: string, callback: ReactSocketEventHandler) => {
        const events = this.events[type];
        if (events && events.all) {
            for (let i = events.all.length - 1; i >= 0; i--) {
                if (events.all[i] === callback) {
                    events.all.splice(i, 1);
                }
            }
        }
    };
    removeListener = (type: string, callback: ReactSocketEventHandler) => {
        this.off(type, callback);
    };
    removeMessageListener = <T extends keyof Namespaces>(
        namespace: T,
        functionName: Namespaces[T],
        callback: ReactSocketEventHandler,
    ) => {
        this.offMessageInternal(namespace, functionName, callback);
    };
    callEvent = (type: string, data: any) => {
        const events = socket.events[type];
        if (events) {
            // Handle handlers trapping all events of that type
            if (events.all) {
                this.asyncRunCallbacks(events.all, data);
            }
            // Handle events bound to a namespace and function
            if (data && data.namespace && data.function) {
                if (!socket.events[type] || !events[data.namespace]) {
                    return;
                }
                const namespaceHandlers = events[data.namespace] as ReactSocketNamespaceEvents;
                const namespaceHandlersFunctions = namespaceHandlers[data.function];
                if (namespaceHandlersFunctions) {
                    this.asyncRunCallbacks(namespaceHandlersFunctions, data);
                }
            }
        }
    };

    asyncRunCallbacks = (callBacks: ReactSocketEventHandler[], data: any) => {
        callBacks.forEach((callback) => {
            (async () => callback(data))();
        });
    };
}

const socket = new Socket() as ReactSocket;

export default socket;
